import { getMonthName } from "@repo/shared";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { tenantUsage } from "@/lib/api/usage";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./page.i18n";

/** Format an EUR amount — more decimals for the small per-event line items. */
function eur(n: number): string {
  return `€${n.toFixed(n !== 0 && Math.abs(n) < 1 ? 4 : 2)}`;
}

/** "2026-06" → "June 2026" / "juni 2026". */
function monthLabel(month: string, lang: string): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return `${getMonthName(m - 1, lang, "long")} ${y}`;
}

export default function TenantUsagePage() {
  const t = useT(T);
  const { userLanguage } = useUserLanguage();
  const [month, setMonth] = useState<string | null>(null);

  const { data: months, isLoading } = useQuery({
    queryKey: ["tenant-usage", "monthly"],
    queryFn: () => tenantUsage.monthly(),
  });

  const { data: breakdown } = useQuery({
    queryKey: ["tenant-usage", "breakdown", month],
    queryFn: () => tenantUsage.breakdown(month as string),
    enabled: !!month,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.title }]}
        title={t.title}
        description={t.description}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t.monthlyCost}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">{t.loading}</p>
          ) : !months || months.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">{t.noUsageYet}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.month}</TableHead>
                  <TableHead className="text-right">{t.cost}</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {months.map((m) => (
                  <TableRow
                    key={m.month}
                    className={`cursor-pointer ${month === m.month ? "bg-muted/50" : ""}`}
                    onClick={() => setMonth(month === m.month ? null : m.month)}
                  >
                    <TableCell className="font-medium">
                      {monthLabel(m.month, userLanguage)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{eur(m.costEur)}</TableCell>
                    <TableCell>
                      <ChevronRight
                        className={`h-4 w-4 text-muted-foreground transition-transform ${month === m.month ? "rotate-90" : ""}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {month && breakdown && (
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t.byFeature(monthLabel(month, userLanguage))}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BreakdownTable
                head={t.feature}
                rows={breakdown.byType.map((r) => ({
                  label: t.types[r.type] ?? r.type,
                  count: r.count,
                  costEur: r.costEur,
                }))}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t.byUser(monthLabel(month, userLanguage))}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BreakdownTable
                head={t.user}
                rows={breakdown.byUser.map((r) => ({
                  label: r.userName,
                  count: r.count,
                  costEur: r.costEur,
                }))}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function BreakdownTable({
  head,
  rows,
}: {
  head: string;
  rows: { label: string; count: number; costEur: number }[];
}) {
  const t = useT(T);
  if (rows.length === 0) return <p className="text-sm text-muted-foreground py-2">{t.noUsage}</p>;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{head}</TableHead>
          <TableHead className="text-right">{t.events}</TableHead>
          <TableHead className="text-right">{t.cost}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.label}>
            <TableCell>{r.label}</TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">
              {r.count}
            </TableCell>
            <TableCell className="text-right tabular-nums">{eur(r.costEur)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
